import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useSignIn } from '@clerk/clerk-react'
import { Input } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'

export default function ForgotPassword() {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const { signIn, isLoaded: clerkLoaded } = useSignIn()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!clerkLoaded || !signIn) return

    setLoading(true)
    setError(null)
    setSuccess(null)

    try {
      await signIn.create({
        strategy: 'reset_password_email_code',
        identifier: email,
      })
      setSuccess('We have sent a password reset code to your email. Check your inbox.')
    } catch (err: any) {
      const msg = err?.errors?.[0]?.longMessage
        || err?.errors?.[0]?.message
        || err?.message
        || 'Failed to send reset code'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 flex items-center justify-center p-4">
      <div className="w-full max-w-md card-glass p-8">
        <div className="text-center mb-6">
          <h2 className="text-xl font-semibold text-slate-800">Reset password</h2>
          <p className="text-sm text-slate-600 mt-1">Enter your email address and we will send you a code to reset your password.</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
        )}
        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">{success}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              className="w-full bg-white border-slate-200"
            />
          </div>

          <Button type="submit" disabled={loading || !clerkLoaded} className="w-full">
            {loading ? 'Sending...' : 'Send reset code'}
          </Button>
        </form>

        {success && (
          <div className="mt-4 text-center">
            <Link
              to="/auth/update-password"
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              I have the code, set new password
            </Link>
          </div>
        )}

        <div className="mt-6 text-center text-sm">
          <Link to="/auth/login" className="text-blue-600 hover:text-blue-700">Back to login</Link>
        </div>
      </div>
    </div>
  )
}
